import React, { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';

const formatWhen = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      timeZone: 'Africa/Maseru',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(value);
  }
};

const AuditLogPanel = ({ showNotification }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resource, setResource] = useState('');
  const [email, setEmail] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '250' });
      if (resource) params.set('resource', resource);
      if (email.trim()) params.set('email', email.trim());
      const res = await api.get(`/system/changes?${params.toString()}`);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Failed to load change history', 'error');
    } finally {
      setLoading(false);
    }
  }, [resource, email, showNotification]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="audit-log-panel">
      <p className="audit-intro">
        Every create, update, delete, approval, and transfer is recorded with <strong>who</strong> did it,
        <strong> what</strong> changed, and <strong>when</strong> (Africa/Maseru time).
      </p>

      <div className="audit-filters">
        <div className="form-group">
          <label className="form-label">Resource</label>
          <select className="form-input" value={resource} onChange={(e) => setResource(e.target.value)}>
            <option value="">All</option>
            <option value="members">Members</option>
            <option value="applications">Applications (via members)</option>
            <option value="transactions">Transactions</option>
            <option value="manual-payers">Manual Payers</option>
            <option value="unemployed-members">Unemployed</option>
            <option value="cdas">CDAS</option>
            <option value="scott-hospital">Scott Hospital</option>
            <option value="system">System / Users</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">User email</label>
          <input
            className="form-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Filter by who…"
          />
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button type="button" className="btn btn-secondary" onClick={load}>Refresh</button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state"><div className="loading-spinner" /><p>Loading change history…</p></div>
      ) : (
        <div className="table-responsive">
          <table className="dashboard-table audit-table">
            <thead>
              <tr>
                <th className="table-header">When</th>
                <th className="table-header">Who</th>
                <th className="table-header">Role</th>
                <th className="table-header">What changed</th>
                <th className="table-header">Status</th>
                <th className="table-header">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td className="table-cell" colSpan={6}>
                    No changes recorded yet. Edits will appear here automatically.
                  </td>
                </tr>
              ) : rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="table-row">
                    <td className="table-cell audit-when">{formatWhen(r.when)}</td>
                    <td className="table-cell">{r.who}</td>
                    <td className="table-cell">
                      <span className={`role-chip ${r.role}`}>{r.role}</span>
                    </td>
                    <td className="table-cell audit-what">{r.what}</td>
                    <td className="table-cell">
                      <span className={`status-badge status-${Number(r.status) < 400 ? 'approved' : 'pending'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="table-cell">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                      >
                        {expanded === r.id ? 'Hide' : 'View'}
                      </button>
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr className="table-row audit-detail-row">
                      <td className="table-cell" colSpan={6}>
                        <pre className="audit-detail-pre">
                          {JSON.stringify(r.details || {}, null, 2)}
                        </pre>
                        <div className="audit-meta">
                          <span>Action: {r.action}</span>
                          <span>Resource: {r.resource || '—'}</span>
                          <span>Method: {r.method}</span>
                          <span>IP: {r.ip || '—'}</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditLogPanel;
